import { sortBy } from 'lodash';
import i18next from 'i18next';
import React from 'react';

import { MenuItem, Select } from '@mui/material';

import { COLUMN_TYPES } from '@src/routes/forecast/Forecast/SalesPlanning/datagrid/constants';
import { renderStandardHeader } from '@src/routes/forecast/Forecast/SalesPlanning/datagrid/customCellRenders';

import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { ALLOWED_INPUT_KEYS_WITH_DECIMAL_SEPARATOR } from '@commons/utils/numericInputKeys';
import { convertEntityQuantityToInpulseUnit, convertUnits } from '@commons/utils/conversion';
import { formatNumber } from '@commons/DisplayNumber';
import { getEntityTotalColumn } from './shared/getEntityTotalColumn';
import { getSharedColumns, handleInputBlur, isFieldModified } from './shared/utils';
import { getTheme } from '@commons/utils/theme';
import { handleDOMFocusChangeOnEnterKeyPress } from './shared/focusHandlers';
import { InputCell, TimeSlotContainer, ToProduceCell } from './styledComponents';
import { NumberInputWithButtons } from './shared/NumberInputWithButtons/NumberInputWithButtons';
import { TimeSlotHeader } from './shared/TimeSlotHeader/TimeSlotHeader';

const CELL_TYPES = {
  FORECAST: 'forecast',
  STOCK: 'stock',
  PRODUCED: 'produced',
};

// Recipe Unit selection constants
export const RECIPE_UNIT_TYPE = {
  BASE_UNIT: 'base_unit',
  BATCH_PRODUCTION: 'batch_production',
};

/**
 * Converts and formats a value based on the recipe unit type.
 * If batch production is enabled, divides the value by the recipe unit quantity.
 *
 * @param {number} convertedValue - The value already converted to Inpulse units
 * @param {string} recipeUnitType - Either RECIPE_UNIT_TYPE.BASE_UNIT or RECIPE_UNIT_TYPE.BATCH_PRODUCTION
 * @param {number} recipeUnitQuantity - The recipe's unit quantity (used for batch production)
 * @param {string} unit - The recipe's base unit (e.g., 'g', 'mL', 'unit')
 * @returns {string} Formatted number with 2 decimal places
 */
const convertAndFormatRecipeValue = (convertedValue, recipeUnitType, recipeUnitQuantity, unit) => {
  if (recipeUnitType === RECIPE_UNIT_TYPE.BATCH_PRODUCTION) {
    const convertedRecipeUnitQuantity = convertEntityQuantityToInpulseUnit(
      recipeUnitQuantity,
      unit,
    );

    return formatNumber(convertedValue / convertedRecipeUnitQuantity, 2);
  }

  return formatNumber(convertedValue, 2);
};

// =============================================================================
// Unit Select Component Styles
// =============================================================================

/**
 * Custom dropdown icon component for the unit Select. Uses the Inpulse dropdown arrow.
 */
const UnitSelectDropdownIcon = () => (
  <img
    src="/images/inpulse/dropdown-down-black-arrow.svg"
    style={{
      width: '16px',
      height: '16px',
      marginRight: '8px',
      cursor: 'default',
    }}
  />
);

/**
 * MenuProps configuration for the unit Select dropdown. Adds shadow and rounded corners.
 */
const UNIT_SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.16)',
      borderRadius: '8px',
      marginTop: '4px',
    },
  },
};

/**
 * Get sx styles for the unit Select component.
 *
 * @param {Object} theme - The application theme object
 * @param {boolean} isEnabled - Whether the Select is enabled (batch production available)
 * @returns {Object} sx prop object for MUI Select
 */
const _getUnitSelectStyles = (theme, isEnabled) => ({
  width: '100%',
  height: '32px',
  backgroundColor: isEnabled ? theme.colors.white : theme.colors.greys.medium,
  font: 'normal 500 14px/16px inter',
  color: isEnabled ? theme.colors.greys.darkest : theme.colors.greys.dark,
  borderRadius: '8px',
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: isEnabled ? theme.colors.greys.darkest : 'transparent',
    borderWidth: '1px',
    borderRadius: '8px',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.colors.greys.darkest,
    borderWidth: '1px',
  },
  '&.Mui-disabled': {
    backgroundColor: theme.colors.greys.lightMedium,
  },
});

/**
 * Get sx styles for the unit MenuItem components.
 *
 * @param {Object} theme - The application theme object
 * @returns {Object} sx prop object for MUI MenuItem
 */
const _getUnitMenuItemStyles = (theme) => ({
  font: 'normal 500 14px/16px inter',
  '&.Mui-selected, &.Mui-selected:hover, &:hover': {
    backgroundColor: theme.colors.greys.lighter,
  },
});

/**
 * Get the dropdown options for unit selection.
 * Will always contain the base recipe unit as the first option.
 * If the recipe has batch production enabled, will also contain the batch unit as the second option.
 *
 * @param {Object} row - The recipe row data
 * @returns {Array} Array of dropdown options
 */
const getUnitDropdownOptions = (row) => {
  const baseUnitLabel = convertUnits(row.unit);
  const convertedUnit = convertUnits(row.unit, true);

  const options = [
    {
      id: RECIPE_UNIT_TYPE.BASE_UNIT,
      name: baseUnitLabel,
      value: baseUnitLabel,
    },
  ];

  if (row.batchProduction && row.unitQuantity) {
    const convertedUnitQuantity = convertEntityQuantityToInpulseUnit(row.unitQuantity, row.unit);
    const formattedUnitQuantity = formatNumber(convertedUnitQuantity, 2);

    options.push({
      id: RECIPE_UNIT_TYPE.BATCH_PRODUCTION,
      name: `x ${formattedUnitQuantity} ${convertedUnit}`,
      value: `x ${formattedUnitQuantity} ${convertedUnit}`,
    });
  }

  return options;
};

/**
 * Get the unit column definition for recipes to allow switching between recipe base unit and batch production (if enabled).
 * Uses MUI Select component as the datagrid pinned column rendered prevents the Inpulse dropdown list from displaying properly.
 *
 * @param {Object} commonColumnsProps - Common column properties
 * @param {Object} recipeUnitSelections - Map of recipeId to selected unit type ('base' | 'batch')
 * @param {Function} onUnitChange - Callback when unit selection changes (recipeId, unitType) => void
 */
const getUnitColumn = (commonColumnsProps, recipeUnitSelections, onUnitChange) => ({
  ...commonColumnsProps,
  field: 'unit',
  headerName: i18next.t('GENERAL.UNIT'),
  minWidth: 150,
  maxWidth: 150,
  width: 150,
  sortable: true,
  resizable: false,
  renderCell: ({ row }) => {
    const theme = getTheme();
    const isEnabled = row.batchProduction === true;
    const options = getUnitDropdownOptions(row);

    // Get stored selection, or default: batch if enabled, base if disabled
    const selectedValue =
      recipeUnitSelections[row.originalRecipeId] ||
      (isEnabled && options.length > 1
        ? RECIPE_UNIT_TYPE.BATCH_PRODUCTION
        : RECIPE_UNIT_TYPE.BASE_UNIT);

    return (
      <Select
        disabled={!isEnabled}
        IconComponent={isEnabled ? UnitSelectDropdownIcon : null}
        MenuProps={UNIT_SELECT_MENU_PROPS}
        size="small"
        sx={_getUnitSelectStyles(theme, isEnabled)}
        value={selectedValue}
        onChange={(e) => onUnitChange(row.originalRecipeId, e.target.value)}
      >
        {options.map((option) => (
          <MenuItem key={option.id} sx={_getUnitMenuItemStyles(theme)} value={option.id}>
            {option.name}
          </MenuItem>
        ))}
      </Select>
    );
  },
});

const handleDOMFocusChangeOnTabKeyPress = (
  rowId,
  timeSlotId,
  onFocusChange,
  timeSlotIndex,
  timeSlots,
) => {
  const nextTimeslotIndex = timeSlotIndex + 1;

  let timeSlotIdToUse = timeSlotId;

  // If nextTimeslotIndex is greater than the length of the timeSlots array we want to focus on the first element of timeslot to have a loop
  if (nextTimeslotIndex >= timeSlots.length) {
    const nextTimeSlotId = timeSlots.length ? timeSlots[0].id : timeSlotId;
    timeSlotIdToUse = nextTimeSlotId;
    // If cell is produced (aka the last cell of the timeslot) we want to focus on the next timeslot
  } else {
    const nextTimeSlotId = timeSlots[nextTimeslotIndex].id;
    timeSlotIdToUse = nextTimeSlotId;
  }

  const nextFocusId = `${rowId}-${timeSlotIdToUse}-${CELL_TYPES.STOCK}`;

  // Find and focus the next input element in the DOM
  const nextInput = document.querySelector(`input[data-focus-id="${nextFocusId}"]`);

  if (nextInput) {
    nextInput.select();
    nextInput.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    onFocusChange(nextFocusId);
  }
};

// Factory function to create keydown handler with proper closure
const createKeyDownHandler =
  ({
    onValueChange,
    rowId,
    timeSlotId,
    onFocusChange,
    timeSlotIndex,
    timeSlots,
    type,
    sortedVisibleRows,
    isForecastDisabled,
    isStockOrProducedDisabled,
  }) =>
  (e) => {
    // Prevent losing focus when using arrow keys
    e.stopPropagation();

    // Handle Enter and Escape keys
    if (e.key === 'Enter') {
      e.preventDefault();

      // Update the focus state
      handleDOMFocusChangeOnEnterKeyPress(
        rowId,
        timeSlotId,
        type,
        onFocusChange,
        sortedVisibleRows,
        'originalRecipeId',
      );

      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();

      // Update the focus state
      handleDOMFocusChangeOnTabKeyPress(
        rowId,
        timeSlotId,
        type,
        onFocusChange,
        timeSlotIndex,
        timeSlots,
        isForecastDisabled,
        isStockOrProducedDisabled,
      );

      return;
    }

    if (e.key === 'Escape') {
      // Cancel and revert to original value
      const originalValue = e.target.dataset.originalValue || '';
      e.target.value = originalValue;
      handleInputBlur(originalValue, onValueChange, rowId, timeSlotId);

      // Leave focus
      e.target.blur();
      onFocusChange(null);

      return;
    }

    // Only allow digits, backspace, delete, and navigation keys
    if (!ALLOWED_INPUT_KEYS_WITH_DECIMAL_SEPARATOR.has(e.key)) {
      e.preventDefault();
    }
  };

const TimeslotCell = ({
  row,
  timeSlotId,
  onStockChange,
  isStockOrProducedDisabled,
  focusedInput,
  onFocusChange,
  originalData,
  timeSlotIndex,
  timeSlots,
  sortedVisibleRows,
  recipeUnitType,
}) => {
  const data = row.dataByTimeSlotIds?.[timeSlotId] || {
    forecast: null,
    inStock: null,
    produced: null,
  };

  const recipeUnitQuantity = row.unitQuantity;

  const convertedForecast = convertEntityQuantityToInpulseUnit(data.forecast, row.unit);
  const convertedInStock =
    data.inStock !== null ? convertEntityQuantityToInpulseUnit(data.inStock, row.unit) : null;
  const convertedToProduce = convertEntityQuantityToInpulseUnit(data.toProduce, row.unit);

  const forecast = convertAndFormatRecipeValue(
    convertedForecast,
    recipeUnitType,
    recipeUnitQuantity,
    row.unit,
  );

  const inStock =
    convertedInStock !== null
      ? convertAndFormatRecipeValue(convertedInStock, recipeUnitType, recipeUnitQuantity, row.unit)
      : null;

  const toProduce = convertAndFormatRecipeValue(
    convertedToProduce,
    recipeUnitType,
    recipeUnitQuantity,
    row.unit,
  );

  // Check if each field has been modified
  const isStockModified = isFieldModified(inStock, originalData, timeSlotId, 'inStock');

  return (
    <TimeSlotContainer>
      {/* Forecast */}
      <InputCell cellType="forecast" isDisabled>
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_12}>
          {forecast}
        </Text>
      </InputCell>

      {/* Stock */}
      <InputCell
        cellType="stock"
        hasBeenModified={isStockModified}
        isDisabled={isStockOrProducedDisabled}
        isFocused={focusedInput === `${row.originalRecipeId}-${timeSlotId}-stock`}
      >
        <NumberInputWithButtons
          createKeyDownHandler={createKeyDownHandler}
          isDisabled={isStockOrProducedDisabled}
          isFocused={focusedInput === `${row.originalRecipeId}-${timeSlotId}-stock`}
          isStockOrProducedDisabled={isStockOrProducedDisabled}
          rowId={row.originalRecipeId}
          sortedVisibleRows={sortedVisibleRows}
          timeSlotId={timeSlotId}
          timeSlotIndex={timeSlotIndex}
          timeSlots={timeSlots}
          type="stock"
          value={inStock}
          allowDecimals
          isForecastDisabled
          onFocusChange={onFocusChange}
          onValueChange={onStockChange}
        />
      </InputCell>

      {/* To produce */}
      <ToProduceCell cellType="toProduce" isDisabled>
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_12}>
          {toProduce >= 0 ? toProduce : 0}
        </Text>
      </ToProduceCell>
    </TimeSlotContainer>
  );
};

const generateRecipeTimeSlotColumns = ({
  timeSlots,
  onStockChange,
  isStockOrProducedDisabled,
  focusedInput,
  onFocusChange,
  originalRecipesData,
  sortedVisibleRows,
  recipeUnitSelections,
}) => {
  // Sort timeSlots by startTime
  const sortedTimeSlots = sortBy(timeSlots, 'startTime');

  return sortedTimeSlots.map((timeSlot, timeSlotIndex) => ({
    field: `timeSlot_${timeSlot.id}`,
    headerName: '', // No headerName because we use renderHeader
    minWidth: 300,
    flex: 1,
    disableColumnMenu: true,
    sortable: false,
    resizable: false,
    filterable: false,
    disableReorder: true,
    renderHeader: () => <TimeSlotHeader showProducedColumn={false} timeSlot={timeSlot} />,
    renderCell: ({ row }) => {
      const originalData = originalRecipesData?.[row.originalRecipeId];

      const recipeUnitType = recipeUnitSelections[row.originalRecipeId];

      return (
        <TimeslotCell
          focusedInput={focusedInput}
          isStockOrProducedDisabled={isStockOrProducedDisabled}
          originalData={originalData}
          recipeUnitType={recipeUnitType}
          row={row}
          sortedVisibleRows={sortedVisibleRows}
          timeSlotId={timeSlot.id}
          timeSlotIndex={timeSlotIndex}
          timeSlots={sortedTimeSlots}
          onFocusChange={onFocusChange}
          onStockChange={onStockChange}
        />
      );
    },
  }));
};

const getRecipeSimpleViewColumns = ({
  singleTimeslotId,
  onStockChange,
  isStockOrProducedDisabled,
  focusedInput,
  onFocusChange,
  commonColumnsProps,
  sortedVisibleRows,
  recipeUnitSelections,
}) => {
  const forecastColumn = {
    ...commonColumnsProps,
    field: 'forecast',
    headerName: i18next.t('PRODUCTION.PLANNING.FORECAST', { count: 1 }),
    flex: 1,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    resizable: false,
    type: 'number',
    valueGetter: (params) => params.row.dataByTimeSlotIds?.[singleTimeslotId]?.forecast,
    renderCell: ({ row }) => {
      const data = (singleTimeslotId && row.dataByTimeSlotIds?.[singleTimeslotId]) || {
        forecast: null,
      };

      const recipeUnitQuantity = row.unitQuantity;
      const recipeUnitType = recipeUnitSelections[row.originalRecipeId];
      const convertedForecast = convertEntityQuantityToInpulseUnit(data.forecast, row.unit);

      const forecast = convertAndFormatRecipeValue(
        convertedForecast,
        recipeUnitType,
        recipeUnitQuantity,
        row.unit,
      );

      return (
        <InputCell cellType="forecast" isDisabled>
          <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_12}>
            {forecast}
          </Text>
        </InputCell>
      );
    },
  };

  const stockColumn = {
    ...commonColumnsProps,
    field: 'stock',
    headerName: i18next.t('PRODUCTION.PLANNING.STOCK'),
    flex: 1,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    resizable: false,
    type: 'number',
    valueGetter: (params) => params.row.dataByTimeSlotIds?.[singleTimeslotId]?.inStock,
    renderCell: ({ row }) => {
      let inStock = null;

      if (singleTimeslotId && row.dataByTimeSlotIds?.[singleTimeslotId]?.inStock >= 0) {
        inStock = row.dataByTimeSlotIds[singleTimeslotId].inStock;
      }

      const recipeUnitQuantity = row.unitQuantity;
      const recipeUnitType = recipeUnitSelections[row.originalRecipeId];
      const convertedStock = convertEntityQuantityToInpulseUnit(inStock, row.unit);

      const formattedInStock = convertAndFormatRecipeValue(
        convertedStock,
        recipeUnitType,
        recipeUnitQuantity,
        row.unit,
      );

      return (
        <InputCell
          cellType="stock"
          hasBeenModified={!!inStock}
          isDisabled={isStockOrProducedDisabled}
          isFocused={focusedInput === `${row.originalRecipeId}-${singleTimeslotId}-stock`}
        >
          <NumberInputWithButtons
            createKeyDownHandler={createKeyDownHandler}
            isDisabled={isStockOrProducedDisabled}
            isFocused={focusedInput === `${row.originalRecipeId}-${singleTimeslotId}-stock`}
            isStockOrProducedDisabled={isStockOrProducedDisabled}
            rowId={row.originalRecipeId}
            sortedVisibleRows={sortedVisibleRows}
            timeSlotId={singleTimeslotId}
            timeSlotIndex={0}
            timeSlots={[]}
            type="stock"
            value={formattedInStock}
            isForecastDisabled
            onFocusChange={onFocusChange}
            onValueChange={onStockChange}
          />
        </InputCell>
      );
    },
  };

  const toProduceColumn = {
    ...commonColumnsProps,
    field: 'toProduce',
    headerName: i18next.t('PRODUCTION.PLANNING.TO_PRODUCE'),
    flex: 1,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    resizable: false,
    type: 'number',
    valueGetter: (params) => {
      const toProduce = getToProduce(params.row.dataByTimeSlotIds?.[singleTimeslotId]);

      return toProduce;
    },
    renderCell: ({ row }) => {
      let toProduce = getToProduce(row.dataByTimeSlotIds?.[singleTimeslotId]);

      const recipeUnitQuantity = row.unitQuantity;
      const recipeUnitType = recipeUnitSelections[row.originalRecipeId];
      const convertedToProduce = convertEntityQuantityToInpulseUnit(toProduce, row.unit);

      const formattedToProduce = convertAndFormatRecipeValue(
        convertedToProduce,
        recipeUnitType,
        recipeUnitQuantity,
        row.unit,
      );

      return (
        <ToProduceCell cellType="toProduce" isDisabled>
          <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_12}>
            {formattedToProduce}
          </Text>
        </ToProduceCell>
      );
    },
  };

  return [forecastColumn, stockColumn, toProduceColumn];
};

const getToProduce = (timeSlotData) => {
  const forecast = timeSlotData?.forecast || 0;
  const inStock = timeSlotData?.inStock || 0;

  // toProduce = forecast - inStock (but minimum 0)
  const toProduce = Math.max(0, forecast - inStock);

  return toProduce;
};

export const getRecipesColumns = ({
  timeSlots = [],
  onStockChange,
  isStockOrProducedDisabled,
  focusedInput,
  onFocusChange,
  originalRecipesData = {},
  formattedRecipesData = [],
  sortedVisibleRows = [],
  recipeUnitSelections = {},
  onUnitChange,
}) => {
  // Determine if we should use simple view (single timeslot without name or no timeslots)
  const useSimpleView = timeSlots.length === 1 && !timeSlots[0].name;

  // Get the single timeslot ID for simple view
  const singleTimeslotId = timeSlots.length === 1 ? timeSlots[0].id : null;

  const commonColumnsProps = {
    disableReorder: true,
    disableColumnMenu: true,
    renderHeader: renderStandardHeader,
  };

  const { nameColumn, categoryColumn } = getSharedColumns(
    commonColumnsProps,
    formattedRecipesData,
    COLUMN_TYPES.RECIPES,
  );

  const unitColumn = getUnitColumn(commonColumnsProps, recipeUnitSelections, onUnitChange);

  if (useSimpleView) {
    const simpleViewColumns = getRecipeSimpleViewColumns({
      singleTimeslotId,
      onStockChange,
      isStockOrProducedDisabled,
      focusedInput,
      onFocusChange,
      commonColumnsProps,
      sortedVisibleRows,
      recipeUnitSelections,
    });

    return [nameColumn, categoryColumn, unitColumn, ...simpleViewColumns];
  } else {
    const timeSlotColumns = generateRecipeTimeSlotColumns({
      timeSlots,
      onStockChange,
      isStockOrProducedDisabled,
      focusedInput,
      onFocusChange,
      originalRecipesData,
      sortedVisibleRows,
      recipeUnitSelections,
    });

    return [
      nameColumn,
      categoryColumn,
      unitColumn,
      ...timeSlotColumns,
      getEntityTotalColumn(commonColumnsProps),
    ];
  }
};
